// What arrives when you mate an enemy army.
//
// Running an army out of moves is the stage boundary in single player: it
// collapses into scrap where it stood, and the board *opens* — a camp's worth
// of new ground beyond the current edge, with a fresh army standing on it,
// often under a colour you have not fought before.
//
// The camps themselves are a table (`formations.ts`), because the ground and
// the army standing on it have to agree, and because a board that grows by
// tacking on rectangles reads as a spreadsheet.
//
// Everything here is deterministic in the stage number and the game's seed,
// for the usual reason: a replayed log has to produce the same waves.

import {
  KING,
  MAX_SIDES,
  pieceColor,
  pieceType,
  ROOK_DIRS,
  type Side,
  square,
  STRIDE
} from "src/engine/rules/board";
import { isAttackedByEnemyOf } from "src/engine/rules/chess/attacks";
import { runIsWon } from "src/engine/rules/campaign/objectives";
import { waveDue, waveIn } from "src/engine/rules/campaign/pending";
import { campStrengthBonus } from "src/engine/rules/campaign/mode";
import { rulesetOf } from "src/engine/rules/ruleset";
import { applyCommand, type GameState } from "src/engine/rules/commands";
import { PIECE_VALUE } from "src/engine/rules/rules";
// The chooser's yardstick. Safe from a cycle: `opponent.ts` does not import
// this module — the waves are generated for it, never by it.
import { evaluate } from "./opponent";
import {
  clonePosition,
  livingSides,
  type Position,
  setCells,
  setPiece,
  isOurs
} from "src/engine/rules/position";
import { type Level, pacedStage, SKILL } from "./difficulty";
import { FORMATIONS, formationFor, placeFormation, reinforce,
  type PlacedCamp
} from "./formations";
import { streamFor } from "./random";

/**
 * How much empty ground sits between the old board and a new camp.
 *
 * **One square.** Enough that neither side is in contact on the turn the camp
 * lands, and enough to lay a lane across; two read as a corridor to walk down
 * rather than a border to fight over, and by the fourth front the walk between
 * them was most of what a turn was spent on.
 */
const APPROACH = 1;

/**
 * How far the joining strip may reach back before it gives up.
 *
 * Not `APPROACH`: a camp is anchored against the ground it is *opposite*, and
 * its far corners can be opposite a bay in a board that has grown ragged. The
 * strip is what keeps the floor continuous, so it has to be allowed to cross
 * one. Bounded so a camp beside a deep inlet paves a bridge rather than a
 * causeway across the whole board.
 */
const BRIDGE_MAX = 6;

/** One square further out, per facing — the direction a camp opens from. */
const APPROACH_STEP = [STRIDE, 1, -STRIDE, -1] as const;

/**
 * How often a new region brings a promotion square, after the first.
 *
 * Not every one: they are the scarce thing the late game is played around,
 * and a guaranteed one per stage would make them a rate rather than a prize.
 */
const PROMOTION_CHANCE = 0.5;

/** How many pawns one promotion square can take before it is spent. */
const PROMOTION_USES = 2;

/** The strongest tier the camp table itself reaches. */
const FORMATION_TIERS = FORMATIONS[FORMATIONS.length - 1].weight;

export interface Wave {
  /** New squares to add to the board — the camp's own ground. */
  cells: number[];
  pieces: [number, number][];
  /** Promotion squares this region reveals, as `[square, uses]`. */
  promotion: [number, number][];
  /** For the announcement: 1 is the force that arrives after the first mate. */
  stage: number;
  /** Which camp arrived, for the announcement. */
  formation: string;
  /** Which army it belongs to. */
  side: Side;
  /**
   * The camp's own industry — ore, refined metal on the floor, pawn facings,
   * buildings.
   *
   * **Empty for a procedural camp, and that is the whole distinction.**
   * `ai/formations.ts` picks pieces out of a table and stands them on a shape,
   * which is all a generated camp has ever been. An **authored** camp is a
   * room somebody drew, and a room is allowed a seam of ore under it and a
   * warehouse on it — land one without them and it is not that camp any more.
   *
   * Optional rather than defaulted to `[]`, because a generated camp has
   * nothing to say here and a field of four empty arrays on every wave is four
   * things a reader has to check are empty.
   */
  heaps?: [square: number, amount: number][];
  metal?: [square: number, grade: number, count: number][];
  facing?: [square: number, direction: number][];
  structures?: [square: number, kind: number, owner: Side][];
}

/**
 * How many fronts have already turned over.
 *
 * **Off the state, not off the log.** It counted `next-stage` commands in the
 * command log, which is this project's save format everywhere except the one
 * place this is read: a run is saved as a *snapshot* (`game/runSave.ts`) and
 * `GameStore.restore` lands one with an **empty log**. So every reader answered
 * nought after a Continue — the next camp was rolled for stage one, at stage
 * one's difficulty, and the header chip agreed
 * with all of it. The board came back and the round did not.
 *
 * The counter is maintained by the `next-stage` reducer at the rollover, beside
 * `runTally` and for the same reason. See `GameState.stage`.
 */
export function stagesCleared(state: GameState): number {
  return state.stage ?? 0;
}

/**
 * Is the side on the clock a beaten enemy — the board a stage boundary fires
 * on?
 *
 * **One predicate, because two drivers answer to it and they were racing.**
 * `useSoloOpponent` reads it to know a stage is turning over; `useRunDirector`
 * reads it to know one is *about to*, and holds its region reveal until it
 * has. They used to fire on independent timers 500ms apart, and the director
 * won: `reveal-region` with a `defeated` side sweeps that army and recomputes
 * the status, so by the time `next-stage` arrived the position was `playing`
 * again and the reducer refused it — correctly, since its gate is the whole of
 * what stops a command that fabricates an army. `GameStore.dispatch` answers
 * `false` and swallows it, so the wave simply never landed and nothing said
 * why. Reported as the second camp not spawning.
 *
 * Mate *or* stalemate: a side that cannot move has been beaten either way, and
 * that is the test the reducer's own gate uses. Never one of ours — an allied
 * army being beaten is a loss, not a cleared front.
 */
export function stageTurnsOver(state: GameState, playerSide: Side): boolean {
  // **A finished run does not get another camp.** Clearing the last objective
  // is the launch, and the front the player was on when they did it is very
  // likely a beaten army — so without this the run ends and immediately grows
  // the board for a fight nobody is going to have.
  if (runIsWon(state)) return false;

  // **A camp that is already bought turns the front over on its clock.** The
  // fight it was bought with ended turns ago — `clear-front` swept that army
  // and handed the clock back — so every test below is about a board that no
  // longer exists: the status is `playing`, and the side on the clock is
  // whoever is still alive rather than anybody beaten.
  //
  // Answered here and *only* here while a camp is owed, so a front cannot be
  // cleared twice over: mating something else during the wait must not land
  // the wave early and must not land a second one.
  //
  // **Except under the spawn clock** (§26 F10), where several enemies live at
  // once and beating one mid-wait is ordinary: the board is frozen in mate
  // with the beaten side on the clock, so something must sweep it, and the
  // mate tests below have to be reachable. Whether a camp lands with the
  // sweep is `spawnHoldsNext`'s half — another enemy standing closes the
  // front without landing, the last one falls through to the ordinary
  // boundary, which lands the owed camp early rather than a second one
  // (`next-stage` clears `pending` either way).
  const owed = waveIn(state);
  if (owed !== null) {
    if (owed === 0) return true;
    if (rulesetOf(state).spawn !== "clock") return false;
  }

  if (state.status !== "checkmate" && state.status !== "stalemate") {
    // **The spawn clock is the third door** (§26 F10): under
    // `ruleset.spawn === "clock"` a camp is due every `RULES.spawnEvery`
    // turns on a board that is still *playing* — nobody beaten, the fight
    // very much on. `waveDue` is the same subtraction the `next-stage` gate
    // reads, the rule this predicate exists for. False everywhere outside a
    // clock-mode run, so the shipped boundary is untouched.
    return waveDue(state);
  }
  if (state.position.turn === playerSide) return false;
  // A beaten enemy turns the boundary over whatever the clocks say — the
  // board is frozen in mate and something must sweep it. Under the spawn
  // clock, *what* happens next is `spawnHoldsNext`'s question: the last
  // enemy falling lands the next camp early (B's rule kept), one of several
  // merely closes its front and the schedule keeps the landing.
  return !state.run || !isOurs(state.position, state.position.turn, state.run.side);
}

/**
 * Which army the next camp flies under.
 *
 * A new colour each time, up to the eight a piece can encode: the board opening
 * onto a camp of a colour you have not fought yet is most of what makes the
 * next stage feel like new ground rather than a reset. Past the table it cycles
 * back to the scenario's own side, which by then is long dead.
 *
 * **Never one of ours, and that is the whole reason this is a function.** It
 * was `1 + (stage % (MAX_SIDES - 1))` written out at the call site — a counter
 * that knows nothing about which sides exist or whose they are. An ally
 * takes the next free index, which in an ordinary run is **2**, and stage one
 * wants exactly 2: the next camp arrives under your own ally's flag, with
 * **no enemies left at all** — nothing can satisfy a `defeat`, no front can
 * turn over (`isOurs` refuses), and `next-stage` files the camp's king where
 * your ally's king was.
 *
 * A living side is skipped too. Reinforcing an army that is still fighting is
 * a different thing from opening a front, and it would clobber that army's
 * king the same way.
 */
export function arrivingSide(state: GameState, stage: number): Side {
  const wanted = (at: number): Side => (1 + (at % (MAX_SIDES - 1))) as Side;
  const position = state.position;
  const run = state.run;

  for (let i = 0; i < MAX_SIDES - 1; i++) {
    const side = wanted(stage + i);
    // An index no army has ever used is free by definition — there is no
    // alliance to read and nobody to clobber.
    if (side >= position.sides) return side;
    if (run && isOurs(position, side, run.side)) continue;
    if (livingSides(position).includes(side)) continue;
    return side;
  }

  // Every colour is spoken for, which takes seven live armies. Take the first
  // that is not ours and let it reinforce.
  for (let i = 0; i < MAX_SIDES - 1; i++) {
    const side = wanted(stage + i);
    if (!run || !isOurs(position, side, run.side)) return side;
  }
  return wanted(stage);
}

/**
 * One camp, built from one stream of rolls.
 *
 * Split out of `nextWave` so that several can be built independently — the
 * whole of `waveOptions`. It takes its randomness as an argument rather than
 * seeding itself, because two camps generated from the same seed and stage
 * have to differ, and the only thing that can make them differ is which stream
 * they are drawing from.
 */
function buildWave(
  state: GameState,
  side: Side,
  stage: number,
  level: Level,
  roll: () => number
): { wave: Wave; facing: number } {
  const skill = SKILL[level];

  // **How hard this front should be, which is not which front it is.** Both
  // the tier a camp may be drawn from and the rank its pawns come back with
  // read the paced stage, so `RULES.stagePace` moves the whole curve at once
  // rather than one counter at a time. Easy's own halving stands on top of it,
  // being a different claim: a beginner's run climbs the same curve slower
  // still.
  const paced = pacedStage(stage);
  // **Hard Mode is one tier on top of all of it** (§26 F13) — the run's
  // constitution, in force from turn one, where the curve above is the
  // opponents' competence climbing. It moves the formation drawn *and* the
  // ranks `reinforce` hands back, which is how the curve already couples them
  // and is what "spawn with more pieces" means: `reinforce` alone only
  // upgrades pawns in a camp of the size the stage would have drawn anyway.
  // Nought on a normal run, so every existing seed builds the camp it always
  // built. `tier` is still capped by `skill.reinforcementTiers` below: how
  // hard a camp may get is the skill's to bound, and this rides underneath it.
  const strength =
    (level === "easy" ? Math.ceil(paced / 2) : paced) + campStrengthBonus(state.run);
  const tier = Math.min(skill.reinforcementTiers, strength);
  // What the player still has to fight with, so a camp can decline to be the
  // wrong size for it. Counted across every side that is not an enemy of the
  // arriving one's enemy — which is to say, everything the player commands,
  // allied armies included.
  let playerPieces = 0;
  for (const piece of state.position.board.values()) {
    if (pieceColor(piece) !== side) playerPieces++;
  }
  // Paced here too, or a camp gated by `minStage` — the siege, the corridor —
  // arrives at the front it always did while
  // everything around it has been slowed down.
  const formation = formationFor({ tier, stage: paced, playerPieces }, roll());
  // Past the end of the table the same camps come back with better pieces in
  // them, so stage six is not stage four again.
  const army = reinforce(formation.pieces, strength - FORMATION_TIERS);

  // Try a few ways round before settling. New ground touches the old, so a
  // rook on an open file can reach straight into a camp — and a wave that
  // arrived in check could arrive *mated*, which since mate is what turns a
  // stage over would be an infinite stage machine rather than a hard fight.
  let fallback: { wave: Wave; facing: number } | null = null;
  for (let attempt = 0; attempt < 8; attempt++) {
    const facing = Math.floor(roll() * 4) & 3;
    const anchor = anchorFor(state.position, facing, roll());
    const camp = placeFormation({ ...formation, pieces: army }, anchor.file, anchor.rank, facing, side);

    // Never pave over ground that already exists, or a square someone is
    // standing on quietly becomes part of a fresh camp.
    const fresh = camp.cells.filter((sq) => !state.position.cells.has(sq));
    // The approach: empty ground between the old edge and the camp, so the
    // new army is a march away rather than already among your pieces. It also
    // gives the industry somewhere to run a lane, which a camp bolted flush
    // against the board never did.
    const bridge = approachTo(state, fresh, facing);
    const cells = [...bridge, ...fresh.filter((sq) => !bridge.includes(sq))];

    const open = new Set(fresh);
    const pieces = camp.pieces.filter(([sq]) => open.has(sq));
    // **And it has to keep its king.** The filter above is silent, and the
    // piece it most often takes is the one the whole front depends on: a camp
    // anchored against ragged ground can overlap squares that already exist,
    // and every piece standing on one is dropped without a word.
    //
    // A kingless camp does not merely fail to be mateable — `gameStatus` reads
    // a kingless side on the clock as **checkmate**, so the front turns over on
    // the turn it lands. What the player sees is a camp arriving and dying by
    // itself, and then the *next* one, a stage harder, for a fight nobody had.
    // Reported from a run. Measured before the check: 8% of single camps and
    // every paired one.
    //
    // A `continue` rather than a repair: there are eight attempts, each with
    // its own facing and anchor, and one of them nearly always lands the camp
    // whole. When none does, the empty wave falls out below and the front gets
    // its ground back — which is what that exists for.
    if (!hasKing(pieces)) continue;

    // New ground has to touch the old. A camp floating in the void is a place
    // the player cannot walk to, so its army can never be reached and its
    // stage can never be cleared — the loop would stop dead at a board that
    // merely looks bigger.
    if (!touchesBoard(state, cells)) continue;

    const wave: Wave = {
      cells,
      pieces,
      // No promotion in a run (§31 P2), so no marker to reveal.
      promotion: state.position.runPawns ? [] : promotionSquares(cells, pieces, bridge, stage, roll),
      stage,
      formation: formation.name,
      side,
      // The camp's own industry, filtered to the ground it actually opened —
      // the same `open` set the pieces are filtered by, and for a sharper
      // reason: a heap landing on an existing square drops scrap on the
      // player's board, and a **building** on one takes a square they are
      // using off the board entirely. A camp may bring its seam; it may not
      // edit the ground that was already there.
      ...campIndustry(camp, open)
    };
    if (fallback === null) fallback = { wave, facing };
    if (!arrivesInCheck(state, wave)) return { wave, facing };
  }

  return (
    fallback ?? {
      wave: {
        cells: [],
        pieces: [],
        promotion: [],
        stage,
        formation: formation.name,
        side
      },
      facing: 0
    }
  );
}

/**
 * A camp, and what a player would want to know about it before taking it.
 *
 * The axes are §19.1's, and every one of them is a number the model already
 * has — which is the whole argument that branching needs no new rules. A
 * *point* in them rather than a score, because the panel (19b) has to name
 * them separately and because collapsing four numbers into one is exactly how
 * two different camps come to look like the same offer.
 */
/**
 * A camp's industry, kept to the ground the camp actually opened.
 *
 * Fields are **absent rather than empty**, because a generated camp has no
 * seam and four empty arrays on every wave is four things a reader has to
 * check are empty — and because `next-stage` skips an absent field rather than
 * placing an empty list.
 */
function campIndustry(
  camp: PlacedCamp,
  open: Set<number>
): Pick<Wave, "heaps" | "metal" | "facing" | "structures"> {
  const keep = <T extends readonly [number, ...unknown[]]>(rows: T[] | undefined) => {
    const kept = (rows ?? []).filter(([sq]) => open.has(sq));
    return kept.length > 0 ? kept : undefined;
  };
  return {
    heaps: keep(camp.heaps),
    metal: keep(camp.metal),
    facing: keep(camp.facing),
    structures: keep(camp.structures)
  };
}

export interface WaveOffer {
  wave: Wave;
  /** Material the camp brings. What "how hard" reads. */
  fight: number;
  /** New squares. Territory is the industrial currency (`holdsGround`). */
  ground: number;
  /** Promotion uses the region reveals. */
  prize: number;
  /** Which way the board grows. A *category*, not a magnitude. */
  facing: number;
}

/**
 * How many camps to generate before choosing between them.
 *
 * More than the two offered, because the choice is made from the ones that
 * survive the dominance test and a dominated camp is not an offer. Four is
 * enough to usually find a pair that disagree and cheap enough to run on the
 * frame a front turns over — this is a *generator*, so the cost lands at the
 * boundary, which is a frame the player is already waiting on.
 */
const CANDIDATES = 4;

/**
 * How many offers a branch presents. Two is a fork and reads instantly; three
 * is a choice and costs a third more generation and a wider panel (§19.9).
 */
export const OFFERS = 2;

/**
 * The stream each candidate draws from.
 *
 * `nextWave` used stream 7 and the offers continue past it, so a run seeded
 * before this existed still generates its first camp from the same numbers.
 * A different `k` per candidate is the same trick `rollLoot` uses to keep two
 * draws independent — and it is what makes the offer set **pure**: derived
 * from the seed and the stage, so a reload cannot re-roll it and nothing has
 * to be stored to keep it honest.
 */
const OFFER_STREAM = 7;

/**
 * The stream the "is this front a pair" roll comes off.
 *
 * **Its own, not another draw off the offers'.** Slipping a draw into the
 * middle of a sequence changes every existing seed's camps, anchors and
 * orientations as a side effect.
 */
export const PAIR_STREAM = 41;

/**
 * The stream a **second front's** camps are drawn from.
 *
 * The pair used to be generated off `OFFER_STREAM` like the first camp — same
 * seed, same stage, same board, same rolls — so it was not a second camp at
 * all: it was the first one again, on the same squares, under a different
 * flag. The reducer places a piece only onto an empty square, so every piece
 * of it was dropped in silence and the second army arrived with nothing in it.
 * Measured on every seed at every paired front: 100% of the second camp's
 * ground shared with the first, 0 pieces surviving.
 *
 * Clear of `OFFER_STREAM + CANDIDATES` and of `PAIR_STREAM` itself, with room
 * for both to grow.
 */
export const PAIR_OFFER_STREAM = 51;

/**
 * Does this camp bring a king?
 *
 * The one thing a camp cannot be placed without. A front turns over when the
 * side on the clock cannot move, and a kingless side reads as *checkmate* the
 * moment it holds the clock — so a camp that lost its king to a placement
 * filter is not a weak camp, it is a stage that ends itself. Asked at every
 * gate a camp passes through rather than at one, because each of them was
 * asking "are there any pieces at all", which a camp of four pawns satisfies.
 */
export const hasKing = (pieces: readonly [number, number][]): boolean =>
  pieces.some(([, piece]) => pieceType(piece) === KING);

/** What a camp is worth as a fight: the material standing in it. */
function fightOf(wave: Wave): number {
  let value = 0;
  for (const [, piece] of wave.pieces) value += PIECE_VALUE[pieceType(piece)];
  return value;
}

/** The offer, measured on every axis. */
function offerOf(wave: Wave, facing: number): WaveOffer {
  return {
    wave,
    fight: fightOf(wave),
    ground: wave.cells.length,
    prize: wave.promotion.reduce((total, [, uses]) => total + uses, 0),
    facing
  };
}

/**
 * Does `a` leave `b` with nothing to argue for?
 *
 * **A dominated offer is not a choice** (§19.6 hazard 1) — if one camp is at
 * least as good on every axis and better on one, the panel presenting both is
 * a delay with two buttons on it.
 *
 * A different **seam is always an argument for itself**, so it is not compared
 * as a magnitude: which way the board grows decides how far the next front is
 * and which of your pieces are near it, and a player can prefer one for
 * reasons no ordering of the other three axes captures. Two camps facing
 * different ways are therefore never dominated by each other, however they
 * compare elsewhere.
 */
/** The same offer twice, as far as a player could tell them apart. */
export const sameOffer = (a: WaveOffer, b: WaveOffer): boolean =>
  a.fight === b.fight &&
  a.ground === b.ground &&
  a.prize === b.prize &&
  a.facing === b.facing;

export function dominates(a: WaveOffer, b: WaveOffer): boolean {
  if (a.facing !== b.facing) return false;
  const axes = ["fight", "ground", "prize"] as const;
  return (
    axes.every((axis) => a[axis] >= b[axis]) && axes.some((axis) => a[axis] > b[axis])
  );
}

/**
 * The camps a cleared front could open onto.
 *
 * `nextWave` generated one and took the first that fitted; this generates
 * several and keeps the ones that genuinely disagree. **Pure and derived from
 * the seed and the stage**, so it survives a reload without being saved and
 * cannot be re-rolled by one — the same argument `runIsWon` makes about not
 * keeping a flag beside a derivable fact.
 *
 * It may legitimately return **one** offer: on a cramped board every candidate
 * can come back the same shape, and fabricating a second that nobody would
 * pick is worse than admitting there is no choice here. 19b's panel has to
 * handle that rather than assume a pair.
 */
export function waveOptions(
  state: GameState,
  side: Side,
  stage: number,
  level: Level,
  count = OFFERS,
  salt = OFFER_STREAM
): WaveOffer[] {
  const candidates: WaveOffer[] = [];
  for (let k = 0; k < CANDIDATES; k++) {
    const built = buildWave(state, side, stage, level, streamFor(state.seed, stage, salt + k));
    // A camp that cannot turn a front over is not an offer — which is a
    // question about its *king*, not about whether anybody is standing in it.
    // (The quiet branch of §19.1 is a *designed* empty camp with its own way of
    // advancing the stage — hazard 6 — and is not this.)
    if (hasKing(built.wave.pieces)) candidates.push(offerOf(built.wave, built.facing));
  }
  if (candidates.length === 0) {
    // Nothing placeable. Fall back to the old behaviour rather than returning
    // nothing, because the caller is at a mate that has to be answered — an
    // empty list here is the run freezing on a board it cannot leave.
    const built = buildWave(state, side, stage, level, streamFor(state.seed, stage, salt));
    return [offerOf(built.wave, built.facing)];
  }

  // Greedy, in generation order, so the set is a function of the seed and not
  // of a sort's tie-breaking: take the first, then each later candidate that
  // neither dominates nor is dominated by anything already taken.
  //
  // **And is not simply the same offer again.** Dominance needs a strict
  // improvement on some axis, so two camps that measure identically on all
  // four dominate each other in neither direction and both used to be taken —
  // which is the purest case of the thing the filter exists to stop. Equality
  // is tested apart from dominance rather than folded into it, because
  // dominance is a real partial order and bending it to mean "or the same"
  // would make `dominates(a, a)` true and every later reader wonder why.
  const offers: WaveOffer[] = [candidates[0]];
  for (const candidate of candidates.slice(1)) {
    if (offers.length >= count) break;
    const argues = offers.every(
      (taken) =>
        !sameOffer(taken, candidate) &&
        !dominates(taken, candidate) &&
        !dominates(candidate, taken)
    );
    if (argues) offers.push(candidate);
  }
  return offers;
}

/**
 * Which camp to land, when there is nobody to ask.
 *
 * **The evaluation, never a coin** (§19.6 hazard 4). Watch mode and the
 * headless rig have no player, and a random pick would make the generator
 * worse than the one it replaces rather than better.
 *
 * *The better fight is the closer one.* Each offer is landed on a clone and
 * priced with `evaluate` from the run side's point of view, and the one whose
 * board comes out nearest to even wins. The two alternatives were both
 * ratchets: taking the board best for the player makes every front easier than
 * the last, and taking the worst makes every front harder, and either way the
 * chooser stops varying — which hazard 4 names as the symptom of offers that
 * are not a choice. Nearest-to-even is self-correcting instead: a player far
 * ahead is offered the camp that closes the gap, and a player struggling is
 * offered the gentler one.
 *
 * Landed through `applyCommand` rather than by hand, so what is priced is the
 * board that would really arrive — and an offer the reducer refuses is not an
 * offer, which is a free guard on a camp that could never have been played.
 */
export function chooseWave(
  state: GameState,
  offers: readonly WaveOffer[],
  side: Side
): Wave {
  if (offers.length <= 1) return offers[0].wave;

  let best: Wave | null = null;
  let closest = Infinity;
  for (const offer of offers) {
    let landed: GameState;
    try {
      landed = applyCommand(state, {
        type: "next-stage",
        side: state.position.turn,
        cells: offer.wave.cells,
        sides: Math.max(state.position.sides, offer.wave.side + 1),
        pieces: offer.wave.pieces,
        promotion: offer.wave.promotion
      });
    } catch {
      // Refused: not an offer. Never the *only* thing standing between the run
      // and a camp, since `best` falls back to the first below.
      continue;
    }
    const gap = Math.abs(evaluate(landed, side));
    if (gap < closest) {
      closest = gap;
      best = offer.wave;
    }
  }
  return best ?? offers[0].wave;
}

/**
 * The camp that lands when a front turns over.
 *
 * Unchanged as an interface and better underneath: it used to generate one
 * camp and take the first orientation that fitted, and now it generates
 * several, keeps the ones that genuinely disagree, and lands the one the
 * evaluation calls the closer fight. **Worth having before the panel exists**
 * (§19.7) — the driver still picks automatically, it just picks better.
 *
 * The side handed in is the *arriving* one; the evaluation is done from the
 * run's own side, because "how good a fight is this" is a question about the
 * player rather than about the army walking on.
 */
export function nextWave(
  state: GameState,
  side: Side,
  stage: number,
  level: Level
): Wave {
  const offers = waveOptions(state, side, stage, level);
  return chooseWave(state, offers, state.run?.side ?? 0);
}

/**
 * Where a new region's promotion squares go, if it has any.
 *
 * Ordinary promotion is off once the board has expanded, so these are the
 * whole of what replaces it: a pawn is *sent* to one rather than marched at a
 * back rank that no longer exists. They are finite on purpose — a permanent
 * one on every new region would make a queen a matter of walking, which is
 * the thing the classic rule stopped meaning the moment the board grew.
 *
 * On the approach rather than in the camp: the corridor is the ground both
 * sides can reach, so the square is contested from the turn it appears
 * instead of sitting behind an army that has to be cleared first. Guaranteed
 * on the first stage, so the mechanic introduces itself, and a seeded roll
 * after that.
 */
function promotionSquares(
  cells: readonly number[],
  pieces: readonly [number, number][],
  bridge: readonly number[],
  stage: number,
  roll: () => number
): [number, number][] {
  if (stage > 1 && roll() >= PROMOTION_CHANCE) return [];

  const taken = new Set(pieces.map(([sq]) => sq));
  // The approach first, then anywhere else the camp opened. Sorted, never Map
  // order: two clients derive this from the same log (PLAN §5.5).
  const open = [...bridge, ...cells].filter((sq) => !taken.has(sq));
  if (open.length === 0) return [];

  const pick = open[Math.floor(roll() * open.length) % open.length];
  return [[pick, PROMOTION_USES]];
}

/** Does any new square share an edge with one already on the board? */
function touchesBoard(state: GameState, cells: readonly number[]): boolean {
  for (const cell of cells) {
    for (const offset of ROOK_DIRS) {
      const at = cell + offset;
      if (!state.position.cells.has(at)) continue;
      return true;
    }
  }
  return false;
}

/**
 * Would this camp's king be under attack the moment it lands?
 *
 * Asked of the board the wave actually arrives on: the new ground added, the
 * beaten army swept off — a dying piece can be the only thing blocking the
 * line that would give check — and the camp standing in place.
 */
function arrivesInCheck(state: GameState, wave: Wave): boolean {
  const probe = clonePosition(state.position);
  setCells(probe, [...probe.cells, ...wave.cells]);

  // Swept only when the side on the clock is genuinely beaten. An on-time
  // landing — a `clear-front` camp coming due, the spawn clock (§26 F10) — arrives on a *playing* board whose clock is
  // very likely the player's, and sweeping their army from the probe answers
  // the question about a board that will never exist: their attacks are what
  // the camp must not land under.
  const beaten = state.status === "checkmate" || state.status === "stalemate";
  if (beaten) {
    for (const [sq, piece] of state.position.board) {
      if (pieceColor(piece) === state.position.turn) setPiece(probe, sq, 0);
    }
  }

  let king = -1;
  for (const [sq, piece] of wave.pieces) {
    setPiece(probe, sq, piece);
    if (pieceType(piece) === KING) king = sq;
  }

  return king >= 0 && isAttackedByEnemyOf(probe, king, wave.side);
}

/**
 * Where a new camp attaches: just outside the current board, on the side it is
 * opening from, at a position along that edge chosen by the roll.
 *
 * One square clear of the edge, so the new ground touches the old rather than
 * overlapping it — the camp's own shape provides the depth.
 */
/**
 * The strip of empty ground joining a camp back to the board.
 *
 * The camp anchors `APPROACH` squares clear of the edge and this fills the
 * gap, so the ground is continuous while the army is a march away. Before
 * this a camp bolted flush against the board put a fresh army within a move
 * of the pieces that had just won the last fight, which read as an ambush
 * rather than a new front — and left nowhere to build.
 *
 * Walked back along the axis the camp opened from, from each of the camp's
 * near-edge squares, until it reaches ground that already exists.
 */
function approachTo(
  state: GameState,
  cells: readonly number[],
  facing: number
): number[] {
  const back = -APPROACH_STEP[facing & 3];
  const out: number[] = [];
  const seen = new Set<number>();

  for (const cell of cells) {
    for (let i = 1; i <= BRIDGE_MAX; i++) {
      const sq = cell + back * i;
      if (state.position.cells.has(sq)) break;
      if (seen.has(sq)) continue;
      seen.add(sq);
      out.push(sq);
    }
  }
  return out;
}

function anchorFor(
  position: Position,
  facing: number,
  roll: number
): { file: number; rank: number } {
  const bounds = position.bounds;
  const spanFile = bounds.maxFile - bounds.minFile;
  const spanRank = bounds.maxRank - bounds.minRank;
  const alongFile = bounds.minFile + Math.floor(roll * (spanFile + 1));
  const alongRank = bounds.minRank + Math.floor(roll * (spanRank + 1));

  // **Against the ground it is opposite, not against the bounding box.**
  //
  // A board that has grown is ragged, and the box is the smallest rectangle
  // that holds it — so on any flank where the ground falls short of the box,
  // anchoring to the box put the camp that much further out than `APPROACH`
  // says, invisibly and cumulatively. Measured over six fronts: the box grew
  // to thirty-nine files across, and most of a turn was the walk between them.
  //
  // Scanning the one line the camp opens along, because that is the ground a
  // player crossing to it would actually leave from. An empty line falls back
  // to the box, which is the old answer and the right one — there is nothing
  // on that line to hug.
  const edgeAlong = (file: number, rank: number, step: number): number | null => {
    let best: number | null = null;
    for (let i = 0; i <= spanFile + spanRank + 2; i++) {
      const sq = square(file, rank) + step * i;
      if (position.cells.has(sq)) best = i;
    }
    return best;
  };

  switch (facing) {
    case 1: {
      const reach = edgeAlong(bounds.minFile, alongRank, 1);
      const from = reach === null ? bounds.maxFile : bounds.minFile + reach;
      return { file: from + 1 + APPROACH, rank: alongRank };
    }
    case 2: {
      const reach = edgeAlong(alongFile, bounds.maxRank, -STRIDE);
      const from = reach === null ? bounds.minRank : bounds.maxRank - reach;
      return { file: alongFile, rank: from - 1 - APPROACH };
    }
    case 3: {
      const reach = edgeAlong(bounds.maxFile, alongRank, -1);
      const from = reach === null ? bounds.minFile : bounds.maxFile - reach;
      return { file: from - 1 - APPROACH, rank: alongRank };
    }
    default: {
      const reach = edgeAlong(alongFile, bounds.minRank, STRIDE);
      const from = reach === null ? bounds.maxRank : bounds.minRank + reach;
      return { file: alongFile, rank: from + 1 + APPROACH };
    }
  }
}
